import {
  Button,
  Create,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useNotify,
  useRefresh,
  type RaRecord,
} from "react-admin";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import BookingItsLookup from "@/components/bookings/BookingItsLookup";
import {
  buildBookingCreateDefaultValues,
  transformBookingCreate,
  validateBookingCreation,
} from "@/utils/booking-create";

function DialogToolbar({ onCancel }: { onCancel: () => void }) {
  return (
    <Toolbar sx={{ bgcolor: "transparent", boxShadow: "none", gap: 1 }}>
      <SaveButton label="Create booking" />
      <Button label="Cancel" sx={{ py: 1, px: 2 }} onClick={onCancel} />
    </Toolbar>
  );
}

export type RazaRequestCreateBookingDialogProps = {
  onClose: () => void;
  razaRequestId: string;
  defaultOrganiser?: string;
  defaultItsNo?: string;
};

/** Embeds booking create on the raza request show page (no new-tab hop). */
const RazaRequestCreateBookingDialog = ({
  onClose,
  razaRequestId,
  defaultOrganiser,
  defaultItsNo,
}: RazaRequestCreateBookingDialogProps) => {
  const refresh = useRefresh();
  const notify = useNotify();

  const defaultValues = buildBookingCreateDefaultValues({
    razaRequestId,
    organiser: defaultOrganiser,
    itsNo: defaultItsNo,
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create hall booking</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Creates a booking linked to this raza request. Add hall slots from this page after the
          booking exists.
        </Typography>
        <Create
          resource="bookings"
          title={false}
          actions={false}
          redirect={false}
          transform={transformBookingCreate}
          sx={{ boxShadow: "none", bgcolor: "transparent", "& .RaCreate-main": { mt: 0 } }}
          mutationOptions={{
            onSuccess: (data: RaRecord) => {
              notify(`Booking created - ${data.id}`, { type: "success" });
              refresh();
              onClose();
            },
            onError: (error: unknown) => {
              const message = error instanceof Error ? error.message : String(error);
              notify(`Could not create booking: ${message}`, { type: "error" });
            },
          }}
        >
          <SimpleForm
            warnWhenUnsavedChanges
            validate={validateBookingCreation}
            defaultValues={defaultValues}
            toolbar={<DialogToolbar onCancel={onClose} />}
          >
            <TextInput source="razaRequestId" sx={{ display: "none" }} />
            <BookingItsLookup />
            <TextInput source="organiser" label="Organiser" fullWidth required />
            <TextInput source="phone" label="Phone" fullWidth required />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This booking is tied to the current raza request.
            </Typography>
          </SimpleForm>
        </Create>
      </DialogContent>
    </Dialog>
  );
};

export default RazaRequestCreateBookingDialog;
